import React from 'react';
import {
  Box,
  Grid,
  Card,
  Stack,
  Divider,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
} from '@mui/material';

import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import { FaCircle } from 'react-icons/fa';

const currencyEGP = (n) => `${Number(n).toLocaleString()} EGP`;

const SectionNumber = ({ n }) => (
  <Box
    sx={{
      width: 22,
      height: 22,
      borderRadius: '50%',
      border: `1.5px solid #3FBEAC`,
      color: "#3FBEAC",
      bgcolor: "#ECF9F7",
      fontSize: 12,
      fontWeight: 700,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}
  >
    {n}
  </Box>
);

const Field = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="#535353" sx={{ display: 'block', mb: 0.5 }}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {value}
    </Typography>
  </Box>
);

const IncludedServicesTab = ({ data }) => {
  const { includedServices, servicesBreakdown, redemptionRules, costBreakdown } = data;

  return (
    <Grid container spacing={2.5} alignItems="stretch">
      {/* Left column */}
      <Grid item xs={12} md={8} sx={{ display: 'flex' }}>
        <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#E8E8E8', p: 1.5, width: '100%', height: '100%' }}>
          {/* Basic Information */}
          <Box my={1} borderBottom="1px solid #E8E8E8" py={2}>
            <Box display="flex" gap={1.5} alignItems="center">
              <SectionNumber n={1} />
              <Typography fontWeight={600}>Basic Information</Typography>
            </Box>
            <Box sx={{ p: { xs: 1, sm: 2 } }}>
              <Grid container spacing={3}>
                <Grid item xs={6} sm={3}>
                  <Field label="Total Included Services" value={`${includedServices.totalIncludedServices} Services`} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Field label="Original Total Price" value={currencyEGP(includedServices.originalTotalPrice)} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Field label="Bundle Price" value={currencyEGP(includedServices.bundlePrice)} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Field label="Total Savings" value={`${currencyEGP(includedServices.totalSavings)} (${includedServices.totalSavingsPercent}% Off)`} />
                </Grid>
              </Grid>
            </Box>
          </Box>

          {/* Services & Items Breakdown */}
          <Box my={1} py={2}>
            <Box sx={{ px: 1, py: .5, my: .5 }}>
              <Box display="flex" gap={1.5} alignItems="center">
                <SectionNumber n={2} />
                <Typography fontWeight={600}>Services & Items Breakdown</Typography>
              </Box>
            </Box>

            <Box sx={{ overflow: 'auto', border: '1px solid #F2F2F2', borderRadius: 3, my: 1,height:300 }}>
              <Table sx={{height:"100%"}} size="small">
                <TableHead>
                  <TableRow>
                    {['Service Name', 'Category', 'Original Price', 'Effective Bundle Price', 'Redemption Limit', 'Status'].map((h) => (
                      <TableCell key={h} sx={{ color: '#535353', fontSize: 12, borderBottom: '1px solid #E8E8E8', textAlign: "center", width: 200 }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {servicesBreakdown.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell sx={{ fontSize: 13, borderBottom: '1px solid #F2F2F2', textAlign: "center", width: 180 }}>{row.name}</TableCell>
                      <TableCell sx={{ fontSize: 13, borderBottom: '1px solid #F2F2F2', textAlign: "center", width: 130 }}>{row.category}</TableCell>
                      <TableCell sx={{ fontSize: 13, borderBottom: '1px solid #F2F2F2', textAlign: "center", width: 130 }}>{currencyEGP(row.originalPrice)}</TableCell>
                      <TableCell sx={{ fontSize: 13, borderBottom: '1px solid #F2F2F2', textAlign: "center", width: 180 }}>{currencyEGP(row.bundlePrice)}</TableCell>
                      <TableCell sx={{ fontSize: 13, borderBottom: '1px solid #F2F2F2', textAlign: "center", width: 130 }}>{row.redemptionLimit}</TableCell>
                      <TableCell sx={{ borderBottom: '1px solid #F2F2F2', textAlign: "center", width: 100 }}>
                        <Chip
                          size="small"
                          label={row.status}
                          sx={{ bgcolor: '#ECF9F7', fontWeight: 600, fontSize: 12, p:1.5 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Box>
        </Card>
      </Grid>

      {/* Right column */}
      <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
          {/* Redemption Rules */}
          <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#E8E8E8', p: 1.5 }}>
            <Box sx={{ p: 1, my: .5, borderBottom: '1px solid #E8E8E8' }}>
              <Typography fontWeight={600}>Redemption Rules</Typography>
            </Box>
            <Stack divider={<Divider />} spacing={1}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1 }}>
                <Typography variant="body2">Validity Period</Typography>
                <Typography variant="body2" fontWeight={600}>{`${redemptionRules.validityPeriod} Days`}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1 }}>
                <Typography variant="body2">Service Usage</Typography>
                <Typography variant="body2" fontWeight={600}>{redemptionRules.serviceUsage}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1 }}>
                <Typography variant="body2">Locations</Typography>
                <Typography variant="body2" fontWeight={600}>{redemptionRules.locations}</Typography>
              </Stack>
            </Stack>
          </Card>

          {/* Cost Breakdown */}
          <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#E8E8E8', p: 1.5, mt: 2, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 1, my: .5, borderBottom: '1px solid #E8E8E8' }}>
              <Typography fontWeight={600}>Cost Breakdown</Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }}  alignItems="center" sx={{ p: 1, flexGrow: 1 }}>
              <Box sx={{ position: 'relative', width: 130, height: 130, flexShrink: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={costBreakdown.segments}
                      dataKey="value"
                      nameKey="label"
                      innerRadius={45}
                      outerRadius={62}
                      paddingAngle={2}
                      startAngle={90}
                      endAngle={-270}
                    >
                      {costBreakdown.segments.map((seg) => (
                        <Cell key={seg.label} fill={seg.color} stroke="none" />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="body2" fontWeight={700} lineHeight={1.1}>
                    {currencyEGP(costBreakdown.total)}
                  </Typography>
                  <Typography variant="caption" color="#535353">
                    Total Support
                  </Typography>
                </Box>
              </Box>

              <Stack spacing={1} flex={1} sx={{ width: '100%' }}>
                {costBreakdown.segments.map((seg) => (
                  <Stack key={seg.label} direction="row" justifyContent="space-between" alignItems="center" py={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <FaCircle size={8} color={seg.color} />
                      <Typography fontSize={14}>{seg.label}</Typography>
                    </Stack>
                    <Typography fontSize={14} color="#535353">
                      {currencyEGP(seg.value)} ({seg.percent}%)
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </Card>
        </Box>
      </Grid>
    </Grid>
  );
};

export default IncludedServicesTab;